import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";
import { supabase } from "@/integrations/supabase/client";

export type ExpenseCategory = "Office" | "Travel" | "Utilities" | "Salary" | "Marketing" | "Miscellaneous";
export type PaymentMethod = "Cash" | "Bank Transfer" | "UPI" | "Credit Card" | "Cheque" | "Other";
export type ExpenseStatus = "Draft" | "Submitted" | "Approved" | "Rejected";

export const EXPENSE_CATEGORIES: ExpenseCategory[] = ["Office", "Travel", "Utilities", "Salary", "Marketing", "Miscellaneous"];
export const PAYMENT_METHODS: PaymentMethod[] = ["Cash", "Bank Transfer", "UPI", "Credit Card", "Cheque", "Other"];

export interface Expense {
  id: string;
  date: string;
  amount: number;
  category: ExpenseCategory;
  vendor: string;
  description: string;
  paymentMethod: PaymentMethod;
  reference?: string;
  receiptUrl?: string; // base64 data URL
  receiptName?: string;
  status: ExpenseStatus;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface MonthlyBreakdown {
  month: string; // "2026-03"
  label: string; // "Mar 2026"
  total: number;
  byCategory: Record<ExpenseCategory, number>;
}

interface ExpenseContextType {
  expenses: Expense[];
  loading: boolean;
  addExpense: (data: Omit<Expense, "id" | "createdBy" | "createdByName" | "createdAt" | "updatedAt" | "status">) => Promise<Expense>;
  updateExpense: (id: string, data: Partial<Expense>) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  approveExpense: (id: string) => Promise<void>;
  rejectExpense: (id: string) => Promise<void>;
  getByCategory: (cat: ExpenseCategory) => Expense[];
  getMonthlyBreakdown: () => MonthlyBreakdown[];
  getTotalByCategory: () => Record<ExpenseCategory, number>;
}

const ExpenseContext = createContext<ExpenseContextType | null>(null);

function mapDbExpense(row: any): Expense {
  return {
    id: row.id,
    date: row.date,
    amount: Number(row.amount),
    category: (row.category as ExpenseCategory) || "Office",
    vendor: row.vendor || "",
    description: row.description || "",
    paymentMethod: (row.payment_method || row.paymentMethod || "Cash") as PaymentMethod,
    reference: row.reference || undefined,
    receiptUrl: row.receipt_url || undefined,
    receiptName: row.receipt_name || undefined,
    status: (row.status as ExpenseStatus) || "Approved",
    createdBy: row.created_by || "",
    createdByName: row.created_by_name || "Admin",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    approvedBy: row.approved_by || undefined,
    approvedAt: row.approved_at || undefined,
  };
}

export function ExpenseProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { activeCompanyId } = useCompany();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);

  // Load real expenses from MySQL scoped strictly to activeCompanyId
  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !user || !activeCompanyId) {
      setExpenses([]);
      setLoading(false);
      return;
    }

    const loadExpenses = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("expenses")
          .select("*")
          .eq("company_id", activeCompanyId)
          .order("date", { ascending: false });

        if (cancelled) return;
        if (!error && Array.isArray(data)) {
          setExpenses(data.map(mapDbExpense));
        } else {
          setExpenses([]);
        }
      } catch (err) {
        console.error("[ExpenseContext] Failed to load expenses from MySQL:", err);
        if (!cancelled) setExpenses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadExpenses();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, activeCompanyId]);

  const addExpense = useCallback(async (data: Omit<Expense, "id" | "createdBy" | "createdByName" | "createdAt" | "updatedAt" | "status">) => {
    const now = new Date().toISOString();
    const tempId = `exp-${Date.now().toString(36)}`;
    const createdBy = user?.id || "admin";
    const createdByName = user?.name || "Admin";
    const targetCompanyId = activeCompanyId || user?.company_id || user?.company;

    if (!targetCompanyId) {
      throw new Error("Cannot add expense without an active company workspace");
    }

    const dbPayload = {
      id: tempId,
      company_id: targetCompanyId,
      date: data.date,
      amount: data.amount,
      category: data.category,
      vendor: data.vendor || null,
      description: data.description || null,
      payment_method: data.paymentMethod,
      reference: data.reference || null,
      receipt_url: data.receiptUrl || null,
      receipt_name: data.receiptName || null,
      status: "Approved",
      created_by: createdBy,
      created_by_name: createdByName,
    };

    const exp: Expense = {
      ...data,
      id: tempId,
      status: "Approved",
      createdBy,
      createdByName,
      createdAt: now,
      updatedAt: now,
    };

    setExpenses((p) => [exp, ...p]);

    try {
      const { data: inserted, error } = await supabase.from("expenses").insert(dbPayload).select().single();
      if (inserted) {
        setExpenses((prev) => prev.map((e) => (e.id === tempId ? mapDbExpense(inserted) : e)));
      }
      if (error) console.error("[ExpenseContext] DB Insert Error:", error);
    } catch (e) {
      console.error("[ExpenseContext] Insert exception:", e);
    }

    return exp;
  }, [user, activeCompanyId]);

  const updateExpense = useCallback(async (id: string, data: Partial<Expense>) => {
    setExpenses((p) => p.map((e) => (e.id === id ? { ...e, ...data, updatedAt: new Date().toISOString() } : e)));

    const dbUpdate: Record<string, any> = { updated_at: new Date().toISOString() };
    if (data.amount !== undefined) dbUpdate.amount = data.amount;
    if (data.category !== undefined) dbUpdate.category = data.category;
    if (data.vendor !== undefined) dbUpdate.vendor = data.vendor;
    if (data.description !== undefined) dbUpdate.description = data.description;
    if (data.paymentMethod !== undefined) dbUpdate.payment_method = data.paymentMethod;
    if (data.date !== undefined) dbUpdate.date = data.date;
    if (data.reference !== undefined) dbUpdate.reference = data.reference;
    if (data.status !== undefined) dbUpdate.status = data.status;

    await supabase.from("expenses").update(dbUpdate).eq("id", id);
  }, []);

  const deleteExpense = useCallback(async (id: string) => {
    setExpenses((p) => p.filter((e) => e.id !== id));
    await supabase.from("expenses").delete().eq("id", id);
  }, []);

  const approveExpense = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    setExpenses((p) => p.map((e) =>
      e.id === id ? { ...e, status: "Approved" as const, approvedBy: user?.id, approvedAt: now, updatedAt: now } : e
    ));

    await supabase.from("expenses").update({
      status: "Approved",
      approved_by: user?.id || null,
      approved_at: now,
      updated_at: now,
    }).eq("id", id);
  }, [user]);

  const rejectExpense = useCallback(async (id: string) => {
    const now = new Date().toISOString();
    setExpenses((p) => p.map((e) =>
      e.id === id ? { ...e, status: "Rejected" as const, updatedAt: now } : e
    ));

    await supabase.from("expenses").update({
      status: "Rejected",
      updated_at: now,
    }).eq("id", id);
  }, []);

  const getByCategory = useCallback((cat: ExpenseCategory) => expenses.filter((e) => e.category === cat), [expenses]);

  const getTotalByCategory = useCallback(() => {
    const result = {} as Record<ExpenseCategory, number>;
    EXPENSE_CATEGORIES.forEach((c) => { result[c] = 0; });
    expenses.filter((e) => e.status !== "Rejected").forEach((e) => { result[e.category] += e.amount; });
    return result;
  }, [expenses]);

  const getMonthlyBreakdown = useCallback((): MonthlyBreakdown[] => {
    const map = new Map<string, MonthlyBreakdown>();
    const approved = expenses.filter((e) => e.status !== "Rejected");
    approved.forEach((e) => {
      const month = e.date.slice(0, 7);
      if (!map.has(month)) {
        const d = new Date(e.date);
        const label = d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
        const byCategory = {} as Record<ExpenseCategory, number>;
        EXPENSE_CATEGORIES.forEach((c) => { byCategory[c] = 0; });
        map.set(month, { month, label, total: 0, byCategory });
      }
      const entry = map.get(month)!;
      entry.total += e.amount;
      entry.byCategory[e.category] += e.amount;
    });
    return Array.from(map.values()).sort((a, b) => b.month.localeCompare(a.month));
  }, [expenses]);

  return (
    <ExpenseContext.Provider value={{
      expenses, loading, addExpense, updateExpense, deleteExpense,
      approveExpense, rejectExpense,
      getByCategory, getMonthlyBreakdown, getTotalByCategory,
    }}>
      {children}
    </ExpenseContext.Provider>
  );
}

export function useExpenses() {
  const ctx = useContext(ExpenseContext);
  if (!ctx) throw new Error("useExpenses must be used within ExpenseProvider");
  return ctx;
}
